<template>
  <div class="floating-scrollbar" v-show="visible" ref="bar"
    @mousedown.prevent="onTrackMousedown">
    <div class="floating-scrollbar__thumb" ref="thumb" :style="thumbStyle"
      @mousedown.stop.prevent="onThumbMousedown"></div>
  </div>
</template>
<script>
/**
 * 浮动横向滚动条：列表过长时，横向滚动条悬浮在视口底部，方便拖动
 * 用法：<floating-scrollbar table-ref="multipleTable"></floating-scrollbar>
 * 需放在 el-table 之后，table-ref 为对应 el-table 的 ref 名称
 */
export default {
  name: "FloatingScrollbar",
  props: {
    tableRef: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      visible: false,
      barWidth: 0,
      thumbWidth: 0,
      thumbLeft: 0,
      dragging: false,
      dragStartX: 0,
      dragStartLeft: 0,
      wrapper: null,
      observer: null
    };
  },
  computed: {
    thumbStyle() {
      return {
        width: this.thumbWidth + "px",
        transform: `translateX(${this.thumbLeft}px)`
      };
    }
  },
  mounted() {
    this.$nextTick(() => {
      const table = this.$parent.$refs[this.tableRef];
      if (table && table.$el) {
        this.wrapper = table.$el.querySelector(".el-table__body-wrapper");
      }
      if (this.wrapper) {
        this.wrapper.addEventListener("scroll", this.syncThumb);
        window.addEventListener("resize", this.syncThumb);
        // 表格数据变化（行增删）时重新计算滚动条
        this.observer = new MutationObserver(() => this.syncThumb());
        this.observer.observe(this.wrapper, { childList: true, subtree: true });
        this.syncThumb();
      }
    });
  },
  beforeDestroy() {
    if (this.wrapper) {
      this.wrapper.removeEventListener("scroll", this.syncThumb);
      window.removeEventListener("resize", this.syncThumb);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    syncThumb() {
      if (!this.wrapper || !this.$refs.bar) return;
      const scrollWidth = this.wrapper.scrollWidth;
      const clientWidth = this.wrapper.clientWidth;
      this.visible = scrollWidth > clientWidth + 1;
      if (!this.visible) return;
      this.barWidth = this.$refs.bar.clientWidth;
      this.thumbWidth = Math.max(
        40,
        (clientWidth / scrollWidth) * this.barWidth
      );
      const maxScrollLeft = scrollWidth - clientWidth;
      const maxBarLeft = this.barWidth - this.thumbWidth;
      this.thumbLeft =
        maxBarLeft > 0
          ? (this.wrapper.scrollLeft / maxScrollLeft) * maxBarLeft
          : 0;
    },
    onThumbMousedown(e) {
      this.dragging = true;
      this.dragStartX = e.clientX;
      this.dragStartLeft = this.thumbLeft;
      document.addEventListener("mousemove", this.onDocumentMousemove);
      document.addEventListener("mouseup", this.onDocumentMouseup);
    },
    onDocumentMousemove(e) {
      if (!this.dragging || !this.wrapper) return;
      const delta = e.clientX - this.dragStartX;
      const maxBarLeft = this.barWidth - this.thumbWidth;
      const newLeft = Math.min(
        maxBarLeft,
        Math.max(0, this.dragStartLeft + delta)
      );
      const maxScrollLeft = this.wrapper.scrollWidth - this.wrapper.clientWidth;
      this.wrapper.scrollLeft =
        maxBarLeft > 0 ? (newLeft / maxBarLeft) * maxScrollLeft : 0;
      this.thumbLeft = newLeft;
    },
    onDocumentMouseup() {
      this.dragging = false;
      document.removeEventListener("mousemove", this.onDocumentMousemove);
      document.removeEventListener("mouseup", this.onDocumentMouseup);
    },
    onTrackMousedown(e) {
      if (!this.wrapper || !this.$refs.bar) return;
      const barRect = this.$refs.bar.getBoundingClientRect();
      const clickX = e.clientX - barRect.left;
      const maxBarLeft = this.barWidth - this.thumbWidth;
      const maxScrollLeft = this.wrapper.scrollWidth - this.wrapper.clientWidth;
      const ratio =
        maxBarLeft > 0 ? (clickX - this.thumbWidth / 2) / maxBarLeft : 0;
      this.wrapper.scrollLeft = Math.max(
        0,
        Math.min(maxScrollLeft, ratio * maxScrollLeft)
      );
    }
  }
};
</script>
<style lang="scss" scoped>
.floating-scrollbar {
  position: sticky;
  bottom: 0;
  height: 10px;
  padding: 1px;
  box-sizing: border-box;
  background: #f5f7fa;
  border: 1px solid #e4e7ed;
  border-top: none;
  border-radius: 0 0 4px 4px;
  cursor: pointer;
  z-index: 10;

  &__thumb {
    height: 100%;
    background: #c0c4cc;
    border-radius: 4px;
    cursor: grab;
    transition: background 0.2s;

    &:hover {
      background: #909399;
    }
  }
}
</style>
